import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Eye, Ban, CheckCircle2, Stethoscope } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DetailDialog, DetailRow } from "@/components/ui/detail-dialog";
import { UserName } from "@/components/common/EntityName";
import { appointmentsApi } from "@/lib/api";
import type { Appointment } from "@/lib/api/types";
import { fmtCurrency, fmtRelativeTime } from "@/lib/utils";
import { usePagedQuery, PAGE_SIZE } from "@/lib/usePagedQuery";

const STATUS_FILTERS = ["ALL", "PENDING", "ACCEPTED", "COMPLETED", "REJECTED", "CANCELLED"] as const;

const STATUS_VARIANT: Record<string, "success" | "warning" | "destructive" | "muted" | "sunset"> = {
  PENDING: "warning",
  ACCEPTED: "sunset",
  COMPLETED: "success",
  REJECTED: "destructive",
  CANCELLED: "muted",
};

function fmtDateTime(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function AppointmentsList() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<(typeof STATUS_FILTERS)[number]>("ALL");
  const [detail, setDetail] = useState<Appointment | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "CANCELLED" | "COMPLETED"; a: Appointment } | null>(null);

  const {
    page, setPage, items, totalItems, totalPages, isFetching,
  } = usePagedQuery<Appointment, { status: string }>({
    key: ["appointments", filter],
    fetcher: appointmentsApi.adminAll,
    extra: { status: filter },
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => appointmentsApi.setStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["appointments"] });
      setConfirm(null);
      setDetail(null);
    },
  });

  return (
    <>
      <PageHeader
        title="Appointments"
        subtitle={`${totalItems} doctor consultations across the platform. Filter, inspect, and force-cancel or complete stuck appointments.`}
      />

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap gap-1.5 border-b border-border p-3">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => { setFilter(f); setPage(0); }}
                className={`cursor-pointer rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                  filter === f
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                {f === "ALL" ? "All" : f.charAt(0) + f.slice(1).toLowerCase()}
              </button>
            ))}
          </div>

          {items.length === 0 ? (
            <EmptyState
              icon={CalendarClock}
              title={isFetching ? "Loading…" : "No appointments"}
              description="No consultations match this filter yet."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead>
                  <TableHead>Doctor</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Fee</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id} className="cursor-pointer" onClick={() => setDetail(a)}>
                    <TableCell className="text-sm font-semibold"><UserName id={a.patientId} /></TableCell>
                    <TableCell className="text-sm"><UserName id={a.doctorId} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDateTime(a.appointmentDate)}</TableCell>
                    <TableCell className="text-sm">
                      {a.consultationFee != null ? fmtCurrency(a.consultationFee, "TZS") : "—"}
                    </TableCell>
                    <TableCell>
                      {a.isPaid ? <Badge variant="success">Paid</Badge> : <Badge variant="muted">Unpaid</Badge>}
                    </TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[a.status] ?? "muted"}>{a.status}</Badge></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {a.status !== "COMPLETED" && a.status !== "CANCELLED" && a.status !== "REJECTED" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setConfirm({ kind: "COMPLETED", a })}>
                              <CheckCircle2 className="size-3.5" /> Complete
                            </Button>
                            <Button size="sm" variant="outline" className="border-destructive/40 text-destructive" onClick={() => setConfirm({ kind: "CANCELLED", a })}>
                              <Ban className="size-3.5" /> Cancel
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setDetail(a)}>
                          <Eye className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Pagination page={page} size={PAGE_SIZE} totalItems={totalItems} totalPages={totalPages} onChange={setPage} />
        </CardContent>
      </Card>

      <DetailDialog
        open={!!detail}
        onClose={() => setDetail(null)}
        icon={Stethoscope}
        title={detail ? fmtDateTime(detail.appointmentDate) : ""}
        subtitle={detail ? `Consultation · ${detail.status}` : undefined}
      >
        {detail && (
          <>
            <div className="mb-2 flex justify-center">
              <Badge variant={STATUS_VARIANT[detail.status] ?? "muted"}>{detail.status}</Badge>
            </div>
            <DetailRow label="Patient" value={<UserName id={detail.patientId} />} />
            <DetailRow label="Doctor" value={<UserName id={detail.doctorId} />} />
            <DetailRow label="Reason" value={detail.reason ?? "—"} />
            <DetailRow label="Fee" value={detail.consultationFee != null ? fmtCurrency(detail.consultationFee, "TZS") : "—"} />
            <DetailRow label="Paid" value={detail.isPaid ? "Yes" : "No"} />
            <DetailRow label="Doctor response" value={detail.doctorResponse ?? "—"} />
            <DetailRow label="Notes" value={detail.notes ?? "—"} />
            <DetailRow label="Booked" value={fmtRelativeTime(detail.createdAt)} />
          </>
        )}
      </DetailDialog>

      <ConfirmDialog
        open={confirm?.kind === "COMPLETED"}
        onOpenChange={(v) => !v && setConfirm(null)}
        onConfirm={() => setStatus.mutate({ id: confirm!.a.id, status: "COMPLETED" })}
        icon={CheckCircle2}
        tone="success"
        title="Mark completed?"
        description="Force the appointment to COMPLETED (e.g. the doctor forgot to close it)."
        confirmLabel="Complete"
        loading={setStatus.isPending}
      />
      <ConfirmDialog
        open={confirm?.kind === "CANCELLED"}
        onOpenChange={(v) => !v && setConfirm(null)}
        onConfirm={() => setStatus.mutate({ id: confirm!.a.id, status: "CANCELLED" })}
        icon={Ban}
        tone="destructive"
        title="Cancel this appointment?"
        description="The appointment is cancelled. If it was paid, handle any refund separately in Transactions."
        confirmLabel="Cancel appointment"
        loading={setStatus.isPending}
      />
    </>
  );
}
